import { useDeferredValue, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock,
  FileStack,
  Hourglass,
  RotateCcw,
  Search,
  Sparkles,
  UserCheck,
  X,
} from "lucide-react";
import { ErrorState } from "../components/PageState";
import { ExportButtons, type ExportFormat } from "../components/ExportButtons";
import { Pager } from "../components/Pager";
import { StatusDot } from "../components/register/ClientRegister";
import {
  appraisalService,
  type AppraisalFilters,
  type AppraisalListItem,
  type AppraisalStatus,
  type DashboardCounts,
} from "../services/appraisalService";
import { aiErrorMessage, aiService } from "../services/aiService";
import { getAll } from "../services/pagination";
import { stageLabel, statusTone } from "../hooks/useAppraisalStages";
import { usePermissions } from "../hooks/usePermissions";
import { useAuthStore } from "../stores/authStore";
import { formatSchoolDate } from "../components/students/studentDisplay";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../utils/exportTable";

const PAGE_SIZE = 20;

/**
 * The dashboard tiles, in the order they are shown.
 *
 * Exported and pure so the mapping from counts to tiles is testable without
 * rendering: a tile pointing at the wrong count is the kind of bug that is
 * invisible until somebody trusts the number.
 */
export interface DashboardTile {
  key: keyof DashboardCounts;
  label: string;
  value: number;
  tone: "neutral" | "progress" | "waiting" | "warning" | "done";
  filter?: AppraisalFilters;
}

export function dashboardTiles(counts: DashboardCounts): DashboardTile[] {
  return [
    { key: "total", label: "Total", value: counts.total, tone: "neutral" },
    {
      key: "awaiting_self_assessment",
      label: "Awaiting self-assessment",
      value: counts.awaiting_self_assessment,
      tone: "progress",
      // The count covers both stages, so the list it opens must too.
      filter: { status: "assigned,self_assessment" },
    },
    {
      key: "awaiting_supervisor_review",
      label: "Awaiting supervisor review",
      value: counts.awaiting_supervisor_review,
      tone: "progress",
      filter: { status: "self_assessment_submitted,supervisor_review" },
    },
    {
      key: "awaiting_approval",
      label: "Awaiting approval",
      value: counts.awaiting_approval,
      tone: "waiting",
      filter: { status: "final_approval" },
    },
    {
      key: "awaiting_acknowledgement",
      label: "Awaiting acknowledgement",
      value: counts.awaiting_acknowledgement,
      tone: "waiting",
      filter: { status: "awaiting_acknowledgement" },
    },
    {
      key: "returned",
      label: "Returned for revision",
      value: counts.returned,
      tone: "warning",
      filter: { status: "returned_for_revision" },
    },
    {
      key: "completed",
      label: "Completed",
      value: counts.completed,
      tone: "done",
      filter: { status: "completed" },
    },
    {
      key: "overdue",
      label: "Overdue",
      value: counts.overdue,
      tone: "warning",
      filter: { overdue: "true" },
    },
  ];
}

/**
 * Turn the overdue-by-stage map into a readable sentence.
 *
 * "8 overdue" tells a head teacher nothing actionable; "6 overdue for
 * self-assessment, 2 for supervisor review" tells them who to chase.
 */
export function describeOverdue(byStage: Record<string, number>): string {
  const parts = Object.entries(byStage)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([stage, count]) => `${count} at ${stageLabel(stage).toLowerCase()}`);
  if (!parts.length) return "Nothing is overdue.";
  return parts.join(", ");
}

/** Drop empty filters so the request carries only what was actually chosen. */
export function buildFilters(
  raw: Record<string, string | undefined>,
  page: number,
  pageSize = PAGE_SIZE,
): AppraisalFilters {
  const filters: AppraisalFilters = { page, page_size: pageSize };
  for (const [key, value] of Object.entries(raw)) {
    if (value !== undefined && value !== "" && value !== "all") {
      (filters as Record<string, unknown>)[key] = value;
    }
  }
  return filters;
}

export function pageCount(total: number, size = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

/**
 * What the reader has to do next on their own appraisal, in a sentence.
 * Their own is the one thing on this page most staff ever need.
 */
export function ownAppraisalStep(item: Pick<AppraisalListItem, "status" | "due_date" | "overall_band">) {
  const due = item.due_date ? ` by ${formatSchoolDate(item.due_date)}` : "";
  switch (item.status) {
    case "assigned":
    case "self_assessment":
      return { yours: true, text: `Your self-assessment is waiting for you${due}.` };
    case "returned_for_revision":
      return { yours: true, text: "It was returned to you for changes. Open it to see what to revise." };
    case "awaiting_acknowledgement":
      return { yours: true, text: "Your result is ready. Read it and acknowledge it." };
    case "completed":
      return { yours: false, text: item.overall_band ? `Complete. Overall result: ${item.overall_band}.` : "Complete." };
    case "final_approval":
      return { yours: false, text: "With the approver for final sign-off." };
    case "draft":
      return { yours: false, text: "Not started yet." };
    default:
      return { yours: false, text: `With your supervisor (${stageLabel(item.status).toLowerCase()})${due}.` };
  }
}

const TONE_DOTS: Record<string, string> = {
  neutral: "bg-gray-400",
  progress: "bg-sky-500",
  waiting: "bg-amber-500",
  warning: "bg-red-500",
  done: "bg-emerald-500",
};

const TILE_ICONS: Record<string, typeof ClipboardList> = {
  total: FileStack,
  awaiting_self_assessment: Clock,
  awaiting_supervisor_review: ClipboardList,
  awaiting_approval: Hourglass,
  awaiting_acknowledgement: UserCheck,
  returned: RotateCcw,
  completed: CheckCircle2,
  overdue: AlertTriangle,
};

const STAGES: AppraisalStatus[] = [
  "assigned",
  "self_assessment",
  "self_assessment_submitted",
  "supervisor_review",
  "supervisor_completed",
  "review_meeting",
  "development_planning",
  "final_approval",
  "awaiting_acknowledgement",
  "completed",
  "returned_for_revision",
];

const ROLES = [
  { key: "", label: "All I can see" },
  { key: "mine", label: "My own" },
  { key: "supervising", label: "I supervise" },
  { key: "approving", label: "I approve" },
];

const SORTS = [
  { key: "-updated", label: "Recently updated" },
  { key: "name", label: "Staff name (A–Z)" },
  { key: "-cycle", label: "Newest cycle first" },
  { key: "status", label: "Stage" },
];

const PAGE_SIZES = [10, 20, 50];
const day = (value?: string | null) => (value ? formatSchoolDate(value) : "—");

type Filters = { role: string; search: string; status: string; cycle: string; overdue: string; ordering: string };
const EMPTY_FILTERS: Filters = { role: "", search: "", status: "all", cycle: "", overdue: "", ordering: "-updated" };

const EXPORT_COLUMNS = [
  { header: "Staff member", accessor: (row: AppraisalListItem) => row.staff_name },
  { header: "Staff number", accessor: (row: AppraisalListItem) => row.staff_employee_id },
  { header: "Department", accessor: (row: AppraisalListItem) => row.department_name },
  { header: "Cycle", accessor: (row: AppraisalListItem) => row.cycle_name },
  { header: "Supervisor", accessor: (row: AppraisalListItem) => row.supervisor_name },
  { header: "Stage", accessor: (row: AppraisalListItem) => stageLabel(row.status) },
  { header: "Due", accessor: (row: AppraisalListItem) => row.due_date ?? "" },
  { header: "Overdue", accessor: (row: AppraisalListItem) => (row.is_overdue ? "Yes" : "No") },
  { header: "Result", accessor: (row: AppraisalListItem) => row.overall_band },
];

function AskAssistant() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [tools, setTools] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async () => {
    if (!question.trim()) return;
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const result = await aiService.ask(question);
      setAnswer(result.answer);
      setTools(result.tools_used);
    } catch (err) {
      setError(aiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-titlebar">
        <div>
          <h2 className="inline-flex items-center gap-2 font-semibold text-gray-900">
            <Sparkles className="h-4 w-4 text-violet-600" aria-hidden />
            Ask about appraisals
          </h2>
          <p className="mt-1 text-sm text-gray-500">The assistant can only reach records you could already open yourself.</p>
        </div>
      </div>
      <div className="space-y-3 px-6 py-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void ask();
          }}
        >
          <label className="block min-w-[16rem] flex-1 text-sm font-medium text-gray-700">
            Your question
            <input
              className="input mt-1"
              value={question}
              placeholder="e.g. Which teachers have not completed their self-assessment?"
              onChange={(event) => setQuestion(event.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy || !question.trim()}>
            <Search className="mr-1 h-4 w-4" aria-hidden />
            {busy ? "Asking…" : "Ask"}
          </button>
        </form>
        {error && <p className="text-sm text-amber-700">{error}</p>}
        {answer && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
            <p className="whitespace-pre-wrap text-sm text-gray-800">{answer}</p>
            {tools.length > 0 && <p className="mt-2 text-xs text-violet-800">Looked up: {tools.join(", ")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/** The reader's own appraisals, and what each needs from them next. */
function MyAppraisal({ items }: { items: AppraisalListItem[] }) {
  return (
    <div className="card">
      <div className="card-titlebar">
        <div>
          <h2 className="font-semibold text-gray-900">Your appraisal</h2>
          <p className="mt-1 text-sm text-gray-500">Where your own appraisal stands and what, if anything, it needs from you.</p>
        </div>
      </div>
      <ul className="divide-y divide-gray-100">
        {items.map((item) => {
          const step = ownAppraisalStep(item);
          return (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{item.cycle_name}</span>
                  <StatusDot label={stageLabel(item.status)} dot={TONE_DOTS[statusTone(item.status)]} />
                  {item.is_overdue && <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700">Overdue</span>}
                </div>
                <p className={`mt-0.5 text-sm ${step.yours ? "font-medium text-primary-800" : "text-gray-600"}`}>{step.text}</p>
                <p className="text-xs text-gray-500">Supervisor: {item.supervisor_name}</p>
              </div>
              <Link to={`/appraisals/${item.id}`} className={step.yours ? "btn btn-primary" : "btn btn-outline"}>
                {step.yours ? "Continue" : "Open"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Appraisals() {
  const navigate = useNavigate();
  const { hasCapability } = usePermissions();
  const userId = useAuthStore((state) => state.user?.id);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);
  // The search reaches the server after a pause, not on every keystroke.
  const search = useDeferredValue(filters.search);

  const dashboard = useQuery({ queryKey: ["appraisal-dashboard"], queryFn: appraisalService.getDashboard });
  const mine = useQuery({
    queryKey: ["appraisals", "mine"],
    queryFn: () => appraisalService.getAppraisals({ role: "mine", page_size: 10, ordering: "-cycle" }),
  });
  const query = useMemo(() => buildFilters({ ...filters, search }, page, pageSize), [filters, search, page, pageSize]);
  const list = useQuery({ queryKey: ["appraisals", query], queryFn: () => appraisalService.getAppraisals(query), keepPreviousData: true });
  const cycles = useQuery({ queryKey: ["appraisal-cycles"], queryFn: appraisalService.getCycles });
  const aiStatus = useQuery({ queryKey: ["ai-status"], queryFn: aiService.getStatus });

  const canSearch = aiStatus.data?.enabled && aiStatus.data.available && aiStatus.data.can_search;
  const canManageCycles = hasCapability("access.appraisal.manage_cycles");
  const canManageTemplates = hasCapability("access.appraisal.manage_templates");

  const own = mine.data?.results ?? [];
  // Someone who sees only their own appraisal gets that, not an empty school-wide dashboard.
  const seesOthers = (dashboard.data?.total ?? 0) > own.length;
  const update = (changes: Partial<Filters>) => {
    setPage(1);
    setFilters((current) => ({ ...current, ...changes }));
  };
  const activeFilters = [filters.search.trim(), filters.status !== "all", filters.cycle, filters.overdue].filter(Boolean).length;
  const tileActive = (tile: DashboardTile) =>
    !filters.role &&
    !filters.cycle &&
    !filters.search.trim() &&
    (tile.filter?.status ?? "all") === filters.status &&
    (tile.filter?.overdue ?? "") === filters.overdue;
  const total = list.data?.count ?? 0;
  const rows = list.data?.results ?? [];
  const first = total ? (page - 1) * pageSize + 1 : 0;

  const exportRows = async (format: ExportFormat) => {
    setExporting(true);
    try {
      // Every page under the same filters, not just the one on screen.
      const everything: Record<string, unknown> = { ...query };
      delete everything.page;
      delete everything.page_size;
      const all = await getAll<AppraisalListItem>("/appraisal/appraisals/", everything);
      if (format === "csv") exportAsCsv("appraisals.csv", EXPORT_COLUMNS, all);
      if (format === "excel") exportAsExcel("appraisals.xls", "Appraisals", EXPORT_COLUMNS, all);
      if (format === "pdf") exportAsPdf("Staff appraisals", EXPORT_COLUMNS, all);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff appraisals</h1>
          <p className="mt-1 text-sm text-gray-500">
            {seesOthers ? "Your own appraisal, and the appraisal rounds you take part in or oversee." : "Your own appraisal and what it needs from you."}
          </p>
        </div>
        <div className="flex gap-2">
          {canManageTemplates && <Link to="/appraisal-templates" className="btn btn-secondary">Manage templates</Link>}
          {canManageCycles && <Link to="/appraisal-templates?tab=cycles" className="btn btn-primary">New cycle</Link>}
        </div>
      </div>

      {mine.isError && <ErrorState message="Your appraisal could not be loaded." retry={() => void mine.refetch()} />}
      {own.length > 0 && <MyAppraisal items={own} />}
      {mine.isSuccess && dashboard.isSuccess && !own.length && !seesOthers && (
        <div className="card">
          <div className="px-6 py-8 text-center">
            <ClipboardList className="mx-auto h-8 w-8 text-gray-300" aria-hidden />
            <h2 className="mt-2 font-semibold text-gray-900">You have no appraisal yet</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
              Your appraisal appears here once the school assigns you to an appraisal round. You will get a notification when it does.
            </p>
          </div>
        </div>
      )}

      {dashboard.isError && <ErrorState message="The appraisal summary could not be loaded." retry={() => void dashboard.refetch()} />}
      {seesOthers && dashboard.data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {dashboardTiles(dashboard.data).map((tile) => {
              const Icon = TILE_ICONS[tile.key as string] ?? ClipboardList;
              const active = tileActive(tile);
              return (
                <button
                  key={tile.key as string}
                  type="button"
                  aria-pressed={active}
                  className={`card text-left transition hover:shadow-md ${active ? "ring-2 ring-primary-500" : ""}`}
                  onClick={() => update({ ...EMPTY_FILTERS, ordering: filters.ordering, status: tile.filter?.status ?? "all", overdue: tile.filter?.overdue ?? "" })}
                  title={tile.filter ? `List these ${tile.value} appraisals` : "Show every appraisal"}
                >
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-500">{tile.label}</span>
                      <Icon className="h-4 w-4 text-gray-400" aria-hidden />
                    </div>
                    <p className={`mt-1 text-2xl font-semibold tabular-nums ${tile.tone === "warning" && tile.value ? "text-red-700" : "text-gray-900"}`}>{tile.value}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {dashboard.data.overdue > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <strong>Overdue:</strong> {describeOverdue(dashboard.data.overdue_by_stage)}
            </div>
          )}
        </>
      )}

      {canSearch && seesOthers && <AskAssistant />}

      {seesOthers && (
        <div className="card">
          <div className="card-header pb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">Appraisals</h3>
                <p className="mt-1 text-sm text-gray-500">Every appraisal you can open. Click one to open it.</p>
              </div>
              <ExportButtons onExport={(format) => void exportRows(format)} disabled={!total} busy={exporting} what="the filtered appraisals" />
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="filter-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Whose appraisals" className="segmented">
                  {ROLES.map((role) => (
                    <button key={role.key} type="button" aria-pressed={filters.role === role.key} onClick={() => update({ role: role.key })} className="segmented-option">
                      {role.label}
                    </button>
                  ))}
                </div>
                {activeFilters > 0 && (
                  <button
                    type="button"
                    onClick={() => update({ search: "", status: "all", cycle: "", overdue: "" })}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                  >
                    <X className="h-3.5 w-3.5" /> Clear {activeFilters} {activeFilters === 1 ? "filter" : "filters"}
                  </button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <label className="filter-field sm:col-span-2 lg:col-span-1">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={filters.search}
                      onChange={(event) => update({ search: event.target.value })}
                      className="input-compact pl-8"
                      placeholder="Name, staff number or supervisor"
                    />
                  </div>
                </label>
                <label className="filter-field">
                  Stage
                  <select value={filters.status} onChange={(event) => update({ status: event.target.value })} className="input-compact">
                    <option value="all">Every stage</option>
                    {/* A tile can choose two stages at once; name that choice too. */}
                    {filters.status !== "all" && !STAGES.includes(filters.status as AppraisalStatus) && (
                      <option value={filters.status}>{filters.status.split(",").map((status) => stageLabel(status)).join(" or ")}</option>
                    )}
                    {STAGES.map((status) => <option key={status} value={status}>{stageLabel(status)}</option>)}
                  </select>
                </label>
                <label className="filter-field">
                  Cycle
                  <select value={filters.cycle} onChange={(event) => update({ cycle: event.target.value })} className="input-compact">
                    <option value="">Every cycle</option>
                    {(cycles.data ?? []).map((cycle) => <option key={cycle.id} value={cycle.id}>{cycle.name}</option>)}
                  </select>
                </label>
                <label className="filter-field">
                  Sort by
                  <select value={filters.ordering} onChange={(event) => update({ ordering: event.target.value })} className="input-compact">
                    {SORTS.map((sort) => <option key={sort.key} value={sort.key}>{sort.label}</option>)}
                  </select>
                </label>
                <label className="filter-field flex items-end gap-2 pb-1.5">
                  <input
                    type="checkbox"
                    checked={filters.overdue === "true"}
                    onChange={(event) => update({ overdue: event.target.checked ? "true" : "" })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span>Overdue only</span>
                </label>
              </div>
            </div>

            {list.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
              </div>
            ) : list.isError ? (
              <ErrorState message="Appraisals could not be loaded." retry={() => void list.refetch()} />
            ) : !rows.length ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                {activeFilters || filters.role ? "No appraisals match these filters." : "No appraisals yet. They appear once staff are assigned to a cycle."}
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`sheet-wrap ${list.isFetching ? "opacity-60" : ""}`}>
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th>Staff member</th>
                        <th>Department</th>
                        <th>Cycle</th>
                        <th>Supervisor</th>
                        <th>Stage</th>
                        <th>Due</th>
                        <th>Result</th>
                        <th className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item, index) => (
                        <tr key={item.id} className="cursor-pointer" onClick={() => navigate(`/appraisals/${item.id}`)}>
                          <td className="sheet-rownum">{first + index}</td>
                          <td>
                            <span className="font-medium text-gray-900">{item.staff_name}</span>
                            {item.staff_user === userId && <span className="ml-1.5 rounded bg-primary-50 px-1 text-[11px] font-medium text-primary-700">You</span>}
                            <span className="ml-1.5 text-xs tabular-nums text-gray-400">{item.staff_employee_id}</span>
                          </td>
                          <td className="text-gray-600">{item.department_name || "—"}</td>
                          <td className="text-gray-600">{item.cycle_name}</td>
                          <td className="text-gray-600">{item.supervisor_name}</td>
                          <td><StatusDot label={stageLabel(item.status)} dot={TONE_DOTS[statusTone(item.status)]} /></td>
                          <td className="tabular-nums">
                            {day(item.due_date)}
                            {item.is_overdue && <span className="ml-1.5 text-xs font-medium text-red-700">overdue</span>}
                          </td>
                          <td className="text-gray-700">{item.overall_band || "—"}</td>
                          <td className="text-right">
                            <Link
                              to={`/appraisals/${item.id}`}
                              onClick={(event) => event.stopPropagation()}
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                            >
                              Open
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  first={first}
                  shown={rows.length}
                  total={total}
                  noun={total === 1 ? "appraisal" : "appraisals"}
                  page={page}
                  pageCount={pageCount(total, pageSize)}
                  onPage={setPage}
                  pageSize={pageSize}
                  pageSizes={PAGE_SIZES}
                  onPageSize={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
